import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { SESSION_STORAGE_KEYS } from '@/lib/local-storage-keys'

export const ENVELOPE_ACCESS_TOKEN_HEADER = 'X-Signater-Envelope-Access-Token'

const RENEWAL_MARGIN_MS = 60_000

interface EnvelopeAccess {
  token: string
  expiresAtUtc: string
}

interface EnvelopeAccessStore {
  tokens: Record<string, EnvelopeAccess>
  setAccess: (envelopeId: string, token: string, expiresAtUtc: string) => void
  clearAccess: (envelopeId: string) => void
}

export const useEnvelopeAccessStore = create<EnvelopeAccessStore>()(
  persist(
    (set) => ({
      tokens: {},

      setAccess: (envelopeId, token, expiresAtUtc) => {
        set((state) => ({
          tokens: { ...state.tokens, [envelopeId]: { token, expiresAtUtc } },
        }))
      },

      clearAccess: (envelopeId) => {
        set((state) => {
          const { [envelopeId]: _removed, ...tokens } = state.tokens
          return { tokens }
        })
      },
    }),
    {
      name: SESSION_STORAGE_KEYS.ENVELOPE_ACCESS,
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
)

const tokenIfValid = (
  access: EnvelopeAccess | undefined,
  now: number,
): string | null =>
  access && Date.parse(access.expiresAtUtc) - now >= RENEWAL_MARGIN_MS
    ? access.token
    : null

export function getEnvelopeAccessToken(
  envelopeId: string,
  now: number = Date.now(),
): string | null {
  return tokenIfValid(useEnvelopeAccessStore.getState().tokens[envelopeId], now)
}

export function discardEnvelopeAccess(envelopeId: string, token: string) {
  const { tokens, clearAccess } = useEnvelopeAccessStore.getState()
  if (Object.hasOwn(tokens, envelopeId) && tokens[envelopeId].token === token)
    clearAccess(envelopeId)
}

export function useEnvelopeAccessToken(
  envelopeId: string | null,
): string | null {
  const access = useEnvelopeAccessStore((state) =>
    envelopeId ? state.tokens[envelopeId] : undefined,
  )
  return tokenIfValid(access, Date.now())
}
